import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PINNED_ASK,
  newlyPinned,
  pinnedAskFor,
  pinnedAskIsOver,
  pinnedIds,
  readPinnedAsk,
} from '../engine/cover/pinnedAsk';
import { isPending } from '../engine/asks';
import { promptDefaults } from '../engine/roster/actedAs';
import { actingCombatantId } from '../engine/turnTracker';
import { infoLine, mergeAll } from './shellCombatant';
import useEscapeKey from './useEscapeKey';

/** A pending Pinned ask, read: its id and question; null for anything else. */
function pendingPinned(ask) {
  if (ask.kind !== PINNED_ASK || !isPending(ask)) return null;
  return { ask, question: readPinnedAsk(ask.payload) };
}

/**
 * Whom to ask about this figure: a player who acts as him, or null for the GM's own prompt - an NPC, a session
 * of one, or a figure hidden from the table (an ask is readable by every member; a hidden name must not ride in one).
 */
function addresseeOf(id, s) {
  if (s.hidden?.hiddenIds?.has(id) || !s.sessionId) return null;
  return promptDefaults([id], s.actedBy ?? {}, s.actingBy ?? {}, s.voxMe?.uid)[id][0] ?? null;
}

/**
 * Open a question for every figure that has just become pinned - on the GM's screen alone, so each is asked once
 * whatever pinned him (suppressing fire, the Pinning special, a hand-set condition).
 */
function useOpenQuestions(s, setLocal) {
  const seen = useRef(null);
  const live = useRef(s);
  live.current = s;
  const { isGm, roster } = s;
  useEffect(() => {
    if (!isGm) return;
    const now = pinnedIds(roster);
    // The first LOADED read is the table as it stood: nobody already pinned is asked again after a reload. An empty
    // roster is not a read - the session's people arrive a render or two after the screen (the review).
    if (!seen.current) {
      if (roster.length) seen.current = now;
      return;
    }
    const fresh = newlyPinned(seen.current, now);
    seen.current = now;
    for (const id of fresh) {
      const question = pinnedAskFor(roster.find((c) => c.id === id));
      const uid = addresseeOf(id, live.current);
      if (uid) live.current.asks.ask(PINNED_ASK, { uid }, question);
      else setLocal((local) => [...local.filter((q) => q.combatantId !== id), question]);
    }
  }, [isGm, roster, setLocal]);
}

/**
 * Each ask cancelled or claimed once (either takes a round trip, and the lists change only when it lands): the
 * Pinned questions that are over, and the answered ones the GM's screen no longer waits for (it reloaded).
 */
function settleAsks({ all, unclaimed }, over, verbs, done) {
  const once = (id) => !done.has(id) && done.add(id);
  const toClose = (all ?? []).map(pendingPinned).filter((p) => p && over(p.question));
  for (const { ask } of toClose) if (once(ask.id)) verbs.cancel(ask.id);
  for (const ask of (unclaimed ?? []).filter((a) => a.kind === PINNED_ASK)) if (once(ask.id)) verbs.claim(ask.id);
}

/** Close what is over - he shook it off, left, or his own turn began - each once. */
function useCloseQuestions({ isGm, roster, asks, turnTracker }, setLocal) {
  const actingId = actingCombatantId(turnTracker?.trackerState);
  const done = useRef(new Set());
  // The verbs through a ref, the LISTS as the dependencies: useAsks hands out a fresh wrapper every render, and an
  // effect keyed on it sent the same delete on every render until the snapshot came back (the review).
  const verbs = useRef(asks);
  verbs.current = asks;
  const { all, unclaimed } = asks;
  useEffect(() => {
    if (!isGm) return;
    const over = (q) => !q || pinnedAskIsOver(q, roster, actingId);
    settleAsks({ all, unclaimed }, over, verbs.current, done.current);
    setLocal((local) => (local.some(over) ? local.filter((q) => !over(q)) : local));
  }, [isGm, roster, all, unclaimed, actingId, setLocal]);
}

/** Every question this screen can answer: addressed to me, the GM's own prompts, and (the GM) anyone's. */
function promptsOf({ isGm, asks }, local) {
  const mine = asks.incoming.filter((a) => a.kind === PINNED_ASK);
  const forOthers = isGm ? (asks.all ?? []).filter((a) => pendingPinned(a) && !mine.includes(a)) : [];
  const fromAsks = [...mine, ...forOthers]
    .map((a) => ({ id: a.id, question: readPinnedAsk(a.payload), forOther: !mine.includes(a) }))
    .filter((p) => p.question);
  return [...fromAsks, ...local.map((q) => ({ id: `local-${q.combatantId}`, question: q, local: true }))];
}

/** What answering does, on the answering screen: Prone writes the condition, Crawl opens the crawl here. */
function act(choice, { combatantId, name }, s, setCrawl) {
  if (choice === 'prone') {
    const proneIn = (st) => ({ ...st, conditions: mergeAll(st.conditions ?? [], ['P'], s.stackPolicy) });
    // Undoable, as the Go Prone plate is (the review): one step back takes him off the ground again.
    s.capturePreActionSnapshot?.();
    s.pushHistory?.();
    s.updateCombatantState(combatantId, proneIn);
    s.addRollNarrative(infoLine(`${name} goes prone - pinned.`));
    return;
  }
  s.setActorId(combatantId);
  setCrawl({ combatantId });
  s.addRollNarrative(infoLine(`${name} crawls for cover - pinned: half his Half Move, nothing from his next turn.`));
}

/**
 * useShellPinned - the Pinned question (2026-10-25): "take cover or go prone, now", put to the pinned figure's
 * player, answered by them or by the GM for them; whoever answers acts on their own screen. Crawl makes the figure
 * this screen's actor and opens the crawl on its map (`crawl`), ended by the crawl's move or Esc.
 *
 * @param {object} s - the shell scope: isGm, roster, asks, actedBy, actingBy, voxMe, hidden, sessionId,
 *   turnTracker, updateCombatantState, addRollNarrative, setActorId, stackPolicy.
 */
export default function useShellPinned(s) {
  const [local, setLocal] = useState([]);
  const [crawl, setCrawl] = useState(null);
  useOpenQuestions(s, setLocal);
  useCloseQuestions(s, setLocal);
  const live = useRef(s);
  live.current = s;
  const { isGm, asks } = s;
  const prompts = useMemo(() => promptsOf({ isGm, asks }, local), [isGm, asks, local]);
  const answer = useCallback((prompt, choice) => {
    act(choice, prompt.question, live.current, setCrawl);
    if (prompt.local) setLocal((l) => l.filter((q) => q.combatantId !== prompt.question.combatantId));
    else live.current.asks.answer(prompt.id, choice);
  }, []);
  const endCrawl = useCallback(() => setCrawl(null), []);
  // Esc leaves the crawl, the figure where it is - CLAIMING the key (useEscapeKey), or the same press also cancelled
  // the attack and cleared the table's focused reaction (the review).
  useEscapeKey(!!crawl, endCrawl);
  return { prompts, answer, crawl, endCrawl };
}

